import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle, memo } from 'react';
import { Entity, PhysicsSystem, RigidbodyComponent, RenderSystem, HTMLfollowComponent, TransformComponent, EmitterComponent, SpiralAnimationComponent, SpiralAnimationSystem, DotImageRenderSystem, ImageContourDot, RoundRecRendererComponent, EmissionSystem, EntityManager, TransformSystem, GravityComponent, RigidbodyFixPosition, FollowSystem, CircleRendererComponent, FollowComponent, BombComponent, BombDetonationSystem, LineRenderComponent } from './CanvasECS.js';
import { getRandomFloatInRange, getImageSize, scaleImage, LoadJSON, getDeviceType } from './Global.js';
import { Vector2, Vector3 } from './geometry.js';

class AnimationData {
    /**
     * @param {[[Vector2]]} imageData - json contour data
     * @param {Vector2} position - image's center position on canvas
     * @param {Vector2} size - image's size in pixels
     * @param {number} visibleTime - image's time on screen
     * @param {Vector3} color - stroke color
     * @param {number} density - image's density.
     * @param {string} tag - image's tag
     */
    constructor(imageData, position, size, visibleTime, color, density, tag) {
        /** @type {[[Vector2]]} */
        this.imageData = imageData;
        /** @type {Vector2} */
        this.position = position;
        /** @type {Vector2} */
        this.size = size;
        /** @type {number} */
        this.visibleTime = visibleTime;
        /** @type {Vector3} */
        this.color = color;
        /** @type {number} */
        this.density = density;
        /** @type {string} */
        this.tag = tag;

        /** @type {[Entity]} */
        this.entities = [];
        /** @type {number} */
        this.state = 0;
        /** @type {boolean} */
        this.isProcessed = false;

        this.timer1 = null;
        this.timer2 = null;
        this.timer3 = null;
        this.timer4 = null;
    }

    /**
     * Hooks entity to this AnimationData instance
     * @param {Entity} entity entity to be hooked
     */
    addEntity(entity){
        this.entities.push(entity);
    }

    clearTimers(){
        if (this.timer1 !== null){
            console.log(`${this.tag} cleared timer1 ${this.timer1}`);
            clearTimeout(this.timer1);
            this.timer1 = null;
        }
        if (this.timer2 !== null){
            console.log(`${this.tag} cleared timer2 ${this.timer2}`);
            clearInterval(this.timer2);
            this.timer2 = null;
        }
        if (this.timer3 !== null){
            console.log(`${this.tag} cleared timer3 ${this.timer3}`);
            clearTimeout(this.timer3);
            this.timer3 = null;
        }
        if (this.timer4 !== null){
            console.log(`${this.tag} cleared timer4 ${this.timer4}`);
            clearTimeout(this.timer4);
            this.timer4 = null;
        }
    }
}
class ImageRecord {
	/**
	 * holds configuration of the image on canvas
	 * @param {string} fileName name of the image JSON file
	 * @param {string} posX position on X of the image in screen pixels or relative units
	 * @param {string} posY position on Y of the image in screen pixels or relative units
	 * @param {Vector2} posAnchor image's pivot point
	 * @param {string | null} width width of the image in screen pixels or relative units
	 * @param {string | null} height height of the image in screen pixels or relative units
	 * @param {number} duration duration in seconds when the image is visible
	 * @param {Vector3} color stroke color
	 * @param {number} density density of the image
	 * @param {string} tag unique tag
	 */
	constructor (fileName, posX, posY, posAnchor, width, height, duration, color, density, tag) {
		/** @type {string} */
		this.fileName = fileName;
		/** @type {string} */
		this.posX = posX;
		/** @type {string} */
		this.posY = posY;
		/** @type {Vector2} */
		this.posAnchor = posAnchor;
		/** @type {string | null} */
		this.width = width;
		/** @type {string | null} */
		this.height = height;
		/** @type {number} */
		this.duration = duration;
		/** @type {Vector3} */
		this.color = color;
		/** @type {number} */
		this.density = density;
		/** @type {string} */
		this.tag = tag;
	}
}

const BackgroundCanvas = forwardRef(({ resolution, backgroundColor, className, interactable=true }, ref) => {
    let windowRatio = window.devicePixelRatio;
    // const windowRatio = 1;

    /**
     * @type {[Map<string, AnimationData>, React.Dispatch<React.SetStateAction<Map<string, AnimationData>>>]}
     */
    const [animationData, setAnimationData] = useState(new Map());
    /**
     * @type {React.MutableRefObject<Map<String, AnimationData>>}
     */
    const animationDataRef = useRef(new Map());

    /** @type {React.MutableRefObject<HTMLCanvasElement | null>} */
    const canvasRef = useRef(null);
    /** @type {React.MutableRefObject<EntityManager | null>} */
    const entities = useRef(null);
    
    /** @type {React.MutableRefObject<PhysicsSystem | null>} */
    const physicsSystem = useRef(null);
    /** @type {React.MutableRefObject<TransformSystem | null>} */
    const transformSystem = useRef(null);
    /** @type {React.MutableRefObject<RenderSystem | null>} */
    const renderSystem = useRef(null);
    /** @type {React.MutableRefObject<SpiralAnimationSystem | null>} */
    const spiralAnimationSystem = useRef(null);
    /** @type {React.MutableRefObject<DotImageRenderSystem | null>} */
    const dotImageRenderSystem = useRef(null);
    /** @type {React.MutableRefObject<EmissionSystem | null>} */
    const emissionSystem = useRef(null);
    /** @type {React.MutableRefObject<FollowSystem | null>} */
    const followSystem = useRef(null);
    /** @type {React.MutableRefObject<BombDetonationSystem | null>} */
    const bombDetonationSystem = useRef(null);

    /** @type {React.MutableRefObject<Entity | null>} */
    const emitter = useRef(null);
    /** @type {React.MutableRefObject<HTMLDivElement | null>} */
    const emitterHTML = useRef(null);
    /** @type {React.MutableRefObject<Entity | null>} */
    const cursor = useRef(null);

    /** @type {boolean} */
    const gamePaused = useRef(false);
    /** @type {boolean} */
    const gamePausedStateChanged = useRef(false);
    /** @type {boolean} */
    const isCanvasVisible = useRef(true);

    const fpsText = useRef(null);
    let deviceType = "PC";


    useEffect(() => {
        animationDataRef.current = animationData;
    }, [animationData]);
    //Canvas initialization
    useEffect(() => {
        console.log("Canvas intialized", resolution);
        deviceType = getDeviceType();
        windowRatio = window.devicePixelRatio;
        const canvas = canvasRef.current;

        entities.current = new EntityManager();
        setAnimationData(new Map());

        // Initialize systems
        physicsSystem.current =         new PhysicsSystem(entities.current, canvas);
        transformSystem.current =       new TransformSystem(entities.current, canvas);
        renderSystem.current =          new RenderSystem(entities.current, canvas);
        dotImageRenderSystem.current =  new DotImageRenderSystem(entities.current, renderSystem.current);
        spiralAnimationSystem.current = new SpiralAnimationSystem(entities.current, canvas);
        emissionSystem.current =        new EmissionSystem(entities.current, canvas);
        followSystem.current =          new FollowSystem(entities.current, canvas);

        if (deviceType === "PC" && interactable){
            bombDetonationSystem.current =  new BombDetonationSystem(entities.current, canvas, renderSystem.current);
            entities.current.onEntityChanged(bombDetonationSystem.current.entityChanged.bind(bombDetonationSystem.current));
        }
        
        entities.current.onEntityChanged(physicsSystem.current.entityChanged.bind(physicsSystem.current));
        entities.current.onEntityChanged(transformSystem.current.entityChanged.bind(transformSystem.current));
        entities.current.onEntityChanged(renderSystem.current.entityChanged.bind(renderSystem.current));
        entities.current.onEntityChanged(dotImageRenderSystem.current.entityChanged.bind(dotImageRenderSystem.current));
        entities.current.onEntityChanged(spiralAnimationSystem.current.entityChanged.bind(spiralAnimationSystem.current));
        entities.current.onEntityChanged(emissionSystem.current.entityChanged.bind(emissionSystem.current));
        entities.current.onEntityChanged(followSystem.current.entityChanged.bind(followSystem.current));

        emitter.current = instantiateEmitter (
            emitterHTML.current,
            new Vector3(255, 255, 255),
            10,
            Math.min(canvas.height * 0.005, 10 * 0.03)
        );
        cursor.current = instantiateCursor (
            new Vector2(-1000, -1000),
            20000
        );        
        
        entities.current.addEntity(emitter.current);
        entities.current.addEntity(cursor.current);

        console.log("Emitter:", emitter.current);
        console.log("Cursor:", cursor.current);
        

        return () => {
            
            animationDataRef.current.forEach((request, tag) => {
                request.clearTimers();
            });
            
            entities.current.clear();

            setAnimationData(new Map());

            physicsSystem.current = null;
            transformSystem.current = null;
            renderSystem.current = null;
            dotImageRenderSystem.current = null;
            spiralAnimationSystem.current = null;
            emissionSystem.current = null;
            followSystem.current = null;
            bombDetonationSystem.current = null;
        };
    }, [resolution]);

    useEffect(() => {
        const canvas = canvasRef.current;
        let didClick = false;
        let isRightClick = false;
        let isCtrlBeingPressed = false;
        let mouseStartPosition = new Vector2(0, 0);
        let bombSpawned = false;
        /** @type {Entity} */
        let bomb = null;
        let cursorTransform = cursor.current.getComponent(TransformComponent);

        if (deviceType !== "PC" || !interactable){
            return;
        }

        const handleContextMenu = (event) => {
            event.preventDefault(); // Prevent the default context menu from appearing
        }
        const handleMouseDown = (e) => {
            if (didClick){
                return;
            }
            e.preventDefault();
            didClick = true;
            isRightClick = false;
            cursor.current.getComponent(CircleRendererComponent).enabled = false;
            if (e.button === 2){
                isRightClick = true;
                cursor.current.getComponent(CircleRendererComponent).enabled = true;
                bomb = null;
                eraseZone();
            } else{
                mouseStartPosition.set(e.clientX, e.clientY);
            }
        };
        const handleMouseMove = (e) => {
            e.preventDefault();
            if (!didClick){
                return;
            }
            if (cursorTransform.position.x < 0 || cursorTransform.position.x > canvasRef.current.width ||
                cursorTransform.position.y < 0 || cursorTransform.position.y > canvasRef.current.height
            ){
                handleMouseUp(e);
                return;
            }
            if (!isRightClick){
                if (!bombSpawned && Vector2.Distance(new Vector2(e.clientX, e.clientY), mouseStartPosition) > 2){
                    bombSpawned = true;
                    bomb = instantiateBomb(cursor.current);
                    entities.current.addEntity(bomb);
                }
            } else{
                eraseZone();
            }
        };
        const handleMouseUp = (e) => {
            e.preventDefault();
            didClick = false;
            if (!isRightClick){
                if (bombSpawned && bomb){
                    bomb.removeComponent(FollowComponent);
                    bomb.removeComponent(LineRenderComponent);
                    bomb.getComponent(BombComponent).enabled = true;
                    bomb.getComponent(TransformComponent).wrapping = true;
                    const bombRb = bomb.getComponent(RigidbodyComponent);
                    bombRb.enabled = true;
                    let force = new Vector2(
                        (mouseStartPosition.x - e.clientX) * 3, 
                        (mouseStartPosition.y - e.clientY) * 3
                    );
                    bombRb.AddForce(force, 1);
    
                    bombSpawned = false;
                    bomb = null;
                } else{
                    if (!isCtrlBeingPressed)
                        spawnGravityObject(cursorTransform.position.copy(), new Vector2(0, 0), new Vector3(98, 0, 204), 20000);        
                    else
                        spawnGravityObject(cursorTransform.position.copy(), new Vector2(0, 0), new Vector3(98, 0, 204), -20000);        
                }
            } else{
                cursor.current.getComponent(CircleRendererComponent).enabled = false;
            }
        };
        const eraseZone = () => {
            let pos = cursor.current.getComponent(TransformComponent).position;
            let found = entities.current.circle(pos, cursor.current.getComponent(CircleRendererComponent).radius);
            found.forEach((ent) => {
                if (ent.id <= 1)
                    return;
                entities.current.deleteEntity(ent);
            });
        };

        const handleKeyDown = (event) => {
            if (event.ctrlKey) {
                isCtrlBeingPressed = true;
            }
        };
        const handleKeyUp = (event) => {
            if (!event.ctrlKey) {
                isCtrlBeingPressed = false;
            }
        };

        canvas.addEventListener('contextmenu', handleContextMenu);
        canvas.addEventListener('mousedown', handleMouseDown);
        window.addEventListener('mousemove', handleMouseMove);
        canvas.addEventListener('mouseup', handleMouseUp);
        document.addEventListener('keydown', handleKeyDown);
        document.addEventListener('keyup', handleKeyUp);
        return () => {
            canvas.removeEventListener('contextmenu', handleContextMenu);
            canvas.removeEventListener('mousedown', handleMouseDown);
            window.removeEventListener('mousemove', handleMouseMove);
            canvas.removeEventListener('mouseup', handleMouseUp);
            document.removeEventListener('keydown', handleKeyDown);
            document.removeEventListener('keyup', handleKeyUp);
        };
    }, [resolution]);


    useEffect(() => {
        const pauseUpdate = () => {
            if (document.hidden || !isCanvasVisible.current/* || !isCanvasOnTop()*/) {
                if (gamePaused.current == false){
                    gamePausedStateChanged.current = true;
                    console.log('CANVAS PAUSED');
                }
                gamePaused.current = true;
            } else {
                if (gamePaused.current == true){
                    gamePausedStateChanged.current = true;
                    console.log('CANVAS UNPAUSED');
                }
                gamePaused.current = false;
            }
        };
    
        const isCanvasOnTop = () => {
            if (!canvasRef.current) return false;
    
            const rect = canvasRef.current.getBoundingClientRect();
            const centerX = rect.left + rect.width / 2;
            const centerY = rect.top + rect.height / 2;
    
            const topElement = document.elementFromPoint(centerX, centerY);
            return topElement === canvasRef.current || canvasRef.current.contains(topElement);
        };
    
        // Intersection Observer for viewport visibility
        const observer = new IntersectionObserver(entries => {
            entries.forEach(entry => {
                isCanvasVisible.current = entry.isIntersecting;
                pauseUpdate();
            });
        });
    
        if (canvasRef.current) {
            observer.observe(canvasRef.current);
        }
    
        document.addEventListener('visibilitychange', pauseUpdate);
    
        // MutationObserver to detect changes in DOM that might cover/uncover canvas
        const mutationObserver = new MutationObserver(() => {
            pauseUpdate();
        });
    
        mutationObserver.observe(document.body, { childList: true, subtree: true, attributes: true });
    
        // Periodic check for safety
        const intervalId = setInterval(() => {
            pauseUpdate();
        }, 500); // Check every 500ms (adjust as needed)
    
        // Cleanup
        return () => {
            document.removeEventListener('visibilitychange', pauseUpdate);
            observer.disconnect();
            mutationObserver.disconnect();
            clearInterval(intervalId);
        };
    }, []);
    
    

    useEffect(() => {
        const canvas = canvasRef.current;
        const fixedDeltaTime = 1 / 60; // 60 updates per second

        let lastTime = performance.now();
        let accumulator = 0;

        let frameID = null;

        const animate = (time) => {
            let gl = canvas.getContext('webgl', { powerPreference: "high-performance" });

            if (!gl) {
                console.error('WebGL not supported, falling back on experimental-webgl');
                gl = canvas.getContext('experimental-webgl');
            }

            if (!gl) {
                console.error('Your browser does not support WebGL');
                return;
            }

            const debugInfo = gl.getExtension("WEBGL_debug_renderer_info");
            const vendor = debugInfo ? gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) : "Unknown";
            const renderer = debugInfo ? gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) : "Unknown";

            // console.log("Vendor:", vendor);
            // console.log("Renderer:", renderer);

            if (!lastTime) lastTime = time;
            let deltaTime = (time - lastTime) / 1000; // Convert to seconds
            if (gamePaused.current){
                frameID = requestAnimationFrame(animate);
                return;
            }
            if (gamePausedStateChanged.current){
                gamePausedStateChanged.current = false;
                deltaTime = 0;
            }

            lastTime = time;

            accumulator += deltaTime;
            
            gl.clearColor(backgroundColor[0], backgroundColor[1], backgroundColor[2], 1); // Set the clear color to black
            gl.clear(gl.COLOR_BUFFER_BIT); // Clear the canvas
            
            // Update systems

            while(accumulator >= fixedDeltaTime){
                spiralAnimationSystem.current.update(fixedDeltaTime);
                physicsSystem.current.update(fixedDeltaTime);
    
                if(bombDetonationSystem.current)
                    bombDetonationSystem.current.update(fixedDeltaTime);
                // followSystem.current.update(fixedDeltaTime);
                // emissionSystem.current.update(fixedDeltaTime);
                
                accumulator -= fixedDeltaTime;
            }
            
            followSystem.current.update(deltaTime);
            // spiralAnimationSystem.current.update(deltaTime);
            // physicsSystem.current.update(deltaTime);
            transformSystem.current.update(deltaTime);
            emissionSystem.current.update(deltaTime);
            renderSystem.current.update(deltaTime);
            dotImageRenderSystem.current.update(deltaTime);
            if (fpsText.current)
                fpsText.current.innerHTML = Math.round(1 / deltaTime);


            frameID = requestAnimationFrame(animate);
        };

        // Start animation loop
        animate(performance.now());

        // Cleanup function
        return () => {
            cancelAnimationFrame(frameID);
        };
    }, [resolution]);

    useEffect(() => {

        animationData.forEach((animationTask, tag) => {
            if (!animationTask.isProcessed){
                const newState = animationTask.state;
                console.log(`State changed for "${tag}" to ${newState}`);
                // console.log(animationTask.density);

                switch (newState) {
                    case 0:
                        console.log(`Animation data "${tag}" initiated`);

                        emitter.current.getComponent(EmitterComponent).CancelEmit(tag);

                        let f = false;
                        let clearedEntities = [];
                        animationTask.entities.forEach(entity => {
                            if (!entity || !entities.current.entities.get(entity.id))
                                return;
                            clearedEntities.push(entity);
                            const data = entity.getComponent(ImageContourDot);
                            if (data){
                                if (data.alphaAcceleration > 0){
                                    data.alphaAcceleration = -2;
                                    f = true;
                                }
                            }
                        });
                        animationTask.entities = clearedEntities;

                        if (f){
                            animationTask.timer1 = setTimeout(() => {
                                const localAnimationTask = animationDataRef.current.get(tag);
                                localAnimationTask.timer1 = null;
                                updateImageTask(tag, 1, null, false);
                            }, 400);
                            updateImageTask(tag, null, null, true);
                        } else{
                            updateImageTask(tag, 1, null, false);
                        }


                        break;
                    case 1:
                        let totalPoints = 0;
                        animationTask.imageData.forEach(contour => {
                            totalPoints += contour.length;
                        });
                        let toSpawn = totalPoints - animationTask.entities.length;
                        console.log(`Spawning points for "${tag}": ${totalPoints} points total. Spawning ${toSpawn} points`);

                        animationTask.entities.forEach(entity => {
                            entity.removeComponent(ImageContourDot);
                            entity.removeComponent(RigidbodyFixPosition);
                        });

                        while(animationTask.entities.length > totalPoints){
                            removeEntity(animationTask.entities[animationTask.entities.length - 1]);
                            animationTask.entities.pop();
                        }

                        const emitComponent = emitter.current.getComponent(EmitterComponent);
                        if (toSpawn > 0){
                            emitComponent.Emit(
                                "staticDot", 
                                tag,
                                toSpawn, 
                                1, 
                                animationTask.color,
                                (emittedEntities) => {
                                    const animationTaskLocal = animationDataRef.current.get(tag);
                                    if (animationTaskLocal === undefined || animationTaskLocal.state !== 1){
                                        emittedEntities.forEach((entity) => {
                                            entities.deleteEntity(entity);
                                        });
                                        console.log(`${emittedEntities.length} Emitted entities removed due to image change`);
                                        return;
                                    }
                                    updateImageTask(tag, 2, emittedEntities.concat(animationTask.entities), false);
                                },
                                null
                            );
                            updateImageTask(tag, null, null, true);
                        } else{
                            updateImageTask(tag, 2, null, false);
                        }

                        
                        break;
                    case 2:
                        console.log(`Loading positions into points for "${tag}"`);
                        let j = 0;

                        emitter.current.getComponent(RoundRecRendererComponent).SetColor(animationTask.color, 3);
            
                        animationTask.imageData.forEach(contour => {
                            //hold edge case
                            animationTask.entities[j + contour.length - 1].addComponent(
                                new ImageContourDot(animationTask.entities[j].id)
                            );
                            
                            for (let z = 0; z < contour.length; z++){
                                const anim = animationTask.entities[j].getComponent(SpiralAnimationComponent);
                                const finalPosition = new Vector2(
                                    contour[z].x + animationTask.position.x, 
                                    contour[z].y + animationTask.position.y
                                );
                                
                                if (z > 0){
                                    animationTask.entities[j - 1].addComponent(
                                        new ImageContourDot(animationTask.entities[j].id)
                                    );
                                }


                                const rb = animationTask.entities[j].getComponent(RigidbodyComponent);
                                if (animationTask.density < 0){
                                    rb.gravitatable = false;
                                } else{
                                    rb.gravitatable = true;
                                }
                                
                                const fixPos = new RigidbodyFixPosition(finalPosition, animationTask.density, false);
                                animationTask.entities[j].addComponent(fixPos);  
                                
                                const animationDuration = Math.round(2 + Math.random() * 3);
                                
                                /* VERY IMPORTANT COLOR SWITCH. MAY WANT TO TURN BACK ON LATER */
                                if (contour[z].r !== undefined){
                                    animationTask.entities[j].getComponent(CircleRendererComponent).SetColor(new Vector3(contour[z].r, contour[z].g, contour[z].b), 5);
                                } else{
                                    animationTask.entities[j].getComponent(CircleRendererComponent).SetColor(animationTask.color.copy(), animationDuration);
                                }
                                
                                
                                anim.Enable(
                                    (Math.random() < 0.5 ? -1 : 1) * (100 + Math.random() * 150), 
                                    finalPosition,
                                    () => {
                                        fixPos.enabled = true;
                                    },
                                    animationDuration
                                );        

                                j++;
                            }
                        });
                        updateImageTask(tag, 3, null, false);
                        break;
                    case 3:
                        console.log(`Waiting for animation to finish for "${tag}"`);
        
                        animationTask.timer2 = setInterval(() => {
                            const animationTask = animationDataRef.current.get(tag);

                            const areAllFinished = animationTask.entities.every(entity => {
                                const anim = entity.getComponent(SpiralAnimationComponent);
                                if (!anim)
                                    return true;
                                return !anim.enabled;
                            });
                
                            if (areAllFinished) {
                                updateImageTask(tag, 4, null, false);
                                clearInterval(animationTask.timer2);
                                animationTask.timer2 = null;
                            }
                        }, 1000); // Check every 1000ms

                        updateImageTask(tag, null, null, true);
                        break;
                    case 4:
                        console.log(`Animation "${tag}" finished. Standing idle for ${animationTask.visibleTime} seconds`);
        
                        if (animationTask.visibleCallback)
                            animationTask.visibleCallback();
            
                        if (animationTask.visibleTime > 0){
                            animationTask.timer3 = setTimeout(() => {
                                const animationTask = animationDataRef.current.get(tag);
                                animationTask.timer3 = null;
    
                                updateImageTask(tag, 5, null, false);
                            }, animationTask.visibleTime * 1000 - 800);
                        }
            
                        updateImageTask(tag, null, null, true);
                        break;
                    case 5:
                        console.log(`Animation "${tag}" is being cleared`);
                        animationTask.entities.forEach(entity => {
                            const data = entity.getComponent(ImageContourDot);
                            if (data)
                                data.alphaAcceleration = -2;

                            entity.removeComponent(RigidbodyFixPosition);
                        });
                        animationTask.timer4 = setTimeout(() => {
                            console.log(`Begin cleaning for "${tag}"`);
                            const animationTask = animationDataRef.current.get(tag);

                            animationTask.timer4 = null;
            
                            if (animationTask.endCallback)
                                animationTask.endCallback();
                            
                            animationTask.entities.forEach(entity => {
                                removeEntity(entity);
                            });
            
                            removeImageTask(tag);
            
                            console.log(`Cleaning for "${tag}" completed`);
            
                        }, 800);
                        updateImageTask(tag, null, null, true);
                        break;

                    default:
                        console.log(`Unknown state change for "${tag} to ${newState}"`);
                }
            }
        });
    }, [animationData]);

    useImperativeHandle(ref, () => ({
        /**
         * Draws an image's contours on the canvas
         * @param {[[Vector2]]} data contours of the image
         * @param {Vector2} position image's center point canvas position
         * @param {Vector2 | null} targetDimensions width and height of the image in screen pixels
         * @param {number} visibleTime number of seconds the image is visible
         * @param {Vector3} color stroke color
         * @param {number} density the density of the image
         * @param {string} tag request tag
         * @returns {boolean} True if added successfully. False if tag already exists, or other error.
         */
        drawPicture(data, position, targetDimensions, visibleTime, color, density, tag) {
            
            let currentSize = getImageSize(data);
            let scalingVector = new Vector2(windowRatio, windowRatio);
            
            if (targetDimensions){
                let scale = new Vector2(targetDimensions.x / currentSize.x, targetDimensions.y / currentSize.y);
                scalingVector.x *= scale.x;
                scalingVector.y *= scale.y;
                currentSize = targetDimensions;
            }

            scaleImage(data, scalingVector);
            position = position.multiply(windowRatio);
            currentSize = currentSize.multiply(windowRatio);
            
            position.x -= currentSize.x / 2;
            position.y -= currentSize.y / 2;

            let newData = new AnimationData(data, position, currentSize, visibleTime, color, density, tag);

            return addImageTask(tag, newData);
        },
        /**
         * Draws an image's contours on the canvas based on record
         * @param {ImageRecord} record the composed data needed for image render
         */
        drawImageRecord(record){
            if (!canvasRef.current){
                return;
            }
            LoadJSON(record.fileName, (data) => {
                let size = getImageSize(data);
                if (record.width !== null && record.height === null){
                    size.scaleSize(Vector2.unit(record.width, this), null);
                } 
                if (record.width === null && record.height !== null){
                    size.scaleSize(null, Vector2.unit(record.height, this));
                }
                if (record.width !== null && record.height !== null){
                    size = new Vector2(record.width, record.height);
                }

                let position = new Vector2(Vector2.unit(record.posX, this), Vector2.unit(record.posY, this));
                position.x = position.x + ((0.5 - record.posAnchor.x) * size.x);
                position.y = position.y + ((0.5 - record.posAnchor.y) * size.y);

                this.drawPicture(data, position, size, record.duration, record.color, record.density, record.tag);
            });
        },
        disbandImage(tag){
            let animationTask = animationData.get(tag);
            
            animationTask.entities.forEach(entity => {
                const data = entity.getComponent(ImageContourDot);
                if (data)
                    data.alphaAcceleration = -2;

                entity.removeComponent(RigidbodyFixPosition);
            });
        },
        prespawnLevel(index, tag, gridPosition, gridSize) {
            const gravCnt = 3;
            // for (let h = 3; h <= 100; h += 94) {
            //     for (let i = 1; i <= gravCnt; i++) {
            //         const mid = Math.ceil(gravCnt / 2);
            //         const mlp = mid - Math.abs(mid - i);
                    
            //         let grEntity = spawnGravityObject(
            //             Vector2.sizeFromText(`${100 / (gravCnt + 1) * i}w%`, `${h}h%`, this).multiply(windowRatio),
            //             new Vector2(0, 0),
            //             new Vector3(98, 0, 204),
            //             1000 * mlp
            //         );
            //     }
            // }
            let magnitude = 2000;
            if (index < 2)
                magnitude *= -1;

            for (let h = 0; h <= gridSize.y; h += gridSize.y) {
                for (let i = 0; i <= gridSize.x; i += gridSize.x) {
                    
                    let grEntity = spawnGravityObject(
                        new Vector2(gridPosition.x + i, gridPosition.y + h).multiply(windowRatio),
                        new Vector2(0, 0),
                        new Vector3(98, 0, 204),
                        magnitude
                    );
                }
            }
            let total = [];
            const totalItems = 4900;
            
            console.log("[Canvas] level size", gridSize);
            
            const columns = Math.floor(Math.sqrt(totalItems * gridSize.x / gridSize.y));
            const rows = Math.ceil(totalItems / columns);
            
            const itemWidth = gridSize.x / columns;
            const itemHeight = gridSize.y / rows;
            
            const paddingX = (gridSize.x - (columns * itemWidth)) / (columns - 1);
            const paddingY = (gridSize.y - (rows * itemHeight)) / (rows - 1);
            
            for (let i = 0; i < totalItems; i++) {
                const entity = new Entity();
                
                const row = Math.floor(i / columns);
                const col = i % columns;
                
                const posX = gridPosition.x + col * (itemWidth + paddingX);
                const posY = gridPosition.y + row * (itemHeight + paddingY);
                
                const velocity = new Vector2(0, 0);
                const transform = new TransformComponent(new Vector2(posX, posY).multiply(windowRatio), false);
                
                entity.addComponent(transform);
                entity.addComponent(new RigidbodyComponent(transform, velocity, 1, 1));
                entity.addComponent(new CircleRendererComponent(new Vector3(157, 157, 157), 3));
                entity.addComponent(new SpiralAnimationComponent(0, new Vector2(0, 0), 3, null, false));
                
                entities.current.addEntity(entity);
                total.push(entity);
            }
            
            const data = new AnimationData(
                null, null, null, null, null, null, tag
            );
            data.entities = total;
            data.state = 4;
            data.isProcessed = true;
            addImageTask(tag, data);
        },       
        contourHTMLObject(objectHTML, padding=0.3) {
            return;
            if (!entities.current) return undefined;
        
            // Get the width and height of the HTML object
            const rect = objectHTML.getBoundingClientRect();
            const width = rect.width;
            const height = rect.height;
            
            const stepDist = Math.max(width * padding, height * padding);
            // Calculate the number of steps based on padding
            const numStepsX = Math.floor(width / stepDist); // Left and right borders
            const numStepsY = Math.floor(height / stepDist); // Top and bottom borders

        
            // Create gravity objects along the borders
            const offsets = [
                new Vector2(0, 0),
                new Vector2(1, 0),
                new Vector2(1, 1),
                new Vector2(0, 1)
            ];
        
            // Function to create entities along a specific edge
            const createEntitiesAlongEdge = (startX, startY, dx, dy, numSteps, isHorizontal) => {
                for (let i = 0; i <= numSteps; i++) {
                    const positionX = startX + dx * (i);
                    const positionY = startY + dy * (i);
                    
                    // Create entity and set components
                    const ent = new Entity();
                    const entTransform = new TransformComponent(new Vector2(0, 0), false); // Adjust position for each entity
                    const entFollow = new HTMLfollowComponent(objectHTML, new Vector2(positionX / width, positionY / height)); // Use normalized position
                    
                    const rend = new CircleRendererComponent(new Vector3(255, 0, 0), 10);
                    const gravRb = new RigidbodyComponent(
                        entTransform, 
                        new Vector2(0, 0),
                        1, 
                        0.99, 
                        false
                    );
                    const entGravity = new GravityComponent(-500);
        
                    // Add components to the entity
                    ent.addComponent(entTransform);
                    ent.addComponent(entFollow);
                    ent.addComponent(gravRb);
                    ent.addComponent(rend);
                    ent.addComponent(entGravity);
        
                    // Add the entity to the entities collection
                    entities.current.addEntity(ent);
                }
            };
        
            // Create entities for each border
            // Top and bottom borders (horizontal edges)
            createEntitiesAlongEdge(0, 0, stepDist, 0, numStepsX, true);   // Top
            createEntitiesAlongEdge(0, height, stepDist, 0, numStepsX, true); // Bottom
        
            // Left and right borders (vertical edges)
            createEntitiesAlongEdge(0, 0, 0, stepDist, numStepsY, false);   // Left
            createEntitiesAlongEdge(width, 0, 0, stepDist, numStepsY, false); // Right
        },               
        spawnGravityObject,
        stopAnimation(tag) {
            interruptAnimation(tag);
        },
        setGravityForce(val){
            cursor.current.getComponent(GravityComponent).magnitude = val;
        },
        getCanvasSize(){
            if (canvasRef.current){
                return new Vector2(canvasRef.current.offsetWidth, canvasRef.current.offsetHeight);
            }
            return new Vector2(0, 0);
        },
        getCanvas(){
            if (canvasRef.current)
                return canvasRef.current;
        }
    }));


    useEffect(() => {

        const handleMouseMovement = (event) => {
            const rect = canvasRef.current.getBoundingClientRect();
            const mousePos = new Vector2(
                (event.clientX - rect.left) * windowRatio,
                (event.clientY - rect.top) * windowRatio
            );
    
            // Update cursor position
            if (cursor.current) {
                const transformComponent = cursor.current.getComponent(TransformComponent);
                if (transformComponent) {
                    transformComponent.position = mousePos;
                } else {
                    console.error("TransformComponent not found in cursor entity");
                }
            } else {
                console.error("Cursor entity is null");
            }
        }
    
        const handleTouchMovement = (event) => {
            if (event.touches.length > 0) {
                const touch = event.touches[0];
                const rect = canvasRef.current.getBoundingClientRect();
                const touchPos = new Vector2(
                    (touch.clientX - rect.left) * windowRatio,
                    (touch.clientY - rect.top) * windowRatio
                );
    
                // Update cursor position
                if (cursor.current) {
                    const transformComponent = cursor.current.getComponent(TransformComponent);
                    if (transformComponent) {
                        transformComponent.position = touchPos;
                    } else {
                        console.error("TransformComponent not found in cursor entity");
                    }
                } else {
                    console.error("Cursor entity is null");
                }
            }
        }
    
        // Add mouse and touch event listeners to the window
        window.addEventListener('mousemove', handleMouseMovement);
        window.addEventListener('touchmove', handleTouchMovement);
    
        // Clean up the event listeners on component unmount
        return () => {
            window.removeEventListener('mousemove', handleMouseMovement);
            window.removeEventListener('touchmove', handleTouchMovement);
        };
    }, [windowRatio, resolution]);
    

    /**
     * Updates image render task's properties
     * @param {string} tag image render task's tag
     * @param {number | null} newState new state
     * @param {[Entity] | null} newEntities new entities array reference
     * @param {boolean | null} newIsProcessed new isProcessed value
     */
    const updateImageTask = (tag, newState, newEntities, newIsProcessed) => {
        setAnimationData((prevData) => {
            if (!prevData.has(tag)) {
                console.error(`"${tag}" animation Data Update Failed - tag not found`);
                return prevData;
            }

            // Create a new Map to ensure immutability
            const newData = new Map(prevData);

            // Get the existing tag data
            const prevTagData = prevData.get(tag);

            // Clone the existing AnimationData instance to ensure immutability
            const updatedTagData = Object.assign(
                Object.create(Object.getPrototypeOf(prevTagData)),
                prevTagData
            );

            // Update the properties of the AnimationData instance
            if (newState !== null) updatedTagData.state = newState;
            if (newEntities !== null) updatedTagData.entities = newEntities;
            if (newIsProcessed !== null) updatedTagData.isProcessed = newIsProcessed;

            if (!updatedTagData.isProcessed) {
                updatedTagData.clearTimers();
            }

            // Set the updated instance back into the new Map
            newData.set(tag, updatedTagData);

            return newData;
        });
    };


    /**
     * Adds new image render task
     * @param {string} tag image render task's tag
     * @param {AnimationData} newData image render task's data
     */
    const addImageTask = (tag, newData) => {
        setAnimationData((prevData) => {
            // Create a new Map to ensure immutability
            const newMap = new Map(prevData);

            if (newMap.has(tag)) {
                const existingData = newMap.get(tag);
                console.warn(`"${tag}" animation reformed. Reused ${existingData.entities.length} points`);

                // Ensure clearTimers method is called on the existing instance
                existingData.clearTimers();

                // Preserve entities from the existing instance
                newData.entities = existingData.entities;
            }

            // Set the new or updated instance in the Map
            newMap.set(tag, newData);

            return newMap;
        });
    };

    /**
     * Stops currently playing animation instantly. Also properly removes it from screen
     * @param {string} tag image render task's tag
     */
    const interruptAnimation = (tag) => {
        if (!(animationData.has(tag))) {
            console.warn(`"${tag}" animation Data interruption Failed - tag doesn't exist`);
            return;
        }
        if (animationData.get(tag).state !== 5){
            console.log(`Interrupting "${tag}"`);
            updateImageTask(tag, 5, null, false);
        }
    }
    /**
     * Removes image render task record by tag from base
     * @param {string} tag image render task's tag
     */
    const removeImageTask = (tag) => {
        setAnimationData((prevData) => {
            if (!prevData.has(tag)) {
                console.log(`"${tag}" animation Data remove Failed - tag not found`);
                return prevData;
            }

            // Create a new Map to ensure immutability
            const newData = new Map(prevData);
            
            // Delete the tag from the new Map
            newData.delete(tag);

            return newData;
        });
    };


    /**
     * Makes entity to move to emitter and self-destroy
     * @param {Entity} entity entity to remove from scene
     */
    const removeEntity = (entity) => {
        entity.removeComponent(ImageContourDot);
        const emitTransform = emitter.current.getComponent(TransformComponent);
        const renderer = emitter.current.getComponent(RoundRecRendererComponent);

        const anim = entity.getComponent(SpiralAnimationComponent);

        anim.Enable(
            anim.radius, 
            new Vector2(
                getRandomFloatInRange(
                    emitTransform.position.x - (renderer.width / 2), 
                    emitTransform.position.x + (renderer.width / 2)
                ), 
                emitTransform.position.y
            ), 
            () => {
                entities.current.deleteEntity(entity);
            }, 
            anim.mxTime
        );
    };

    // useEffect(() => {
    //     if (canvasRef.current) {
    //       observer.current = new ResizeObserver((entries) => {
    //         if (entries[0].contentRect) {
    //           canvasRef.current.width = entries[0].contentRect.width * windowRatio;
    //           canvasRef.current.height = entries[0].contentRect.height * windowRatio;
    //         }
    //       });
    
    //       observer.current.observe(canvasRef.current);
    //     }
    
    //     return () => {
    //       if (observer.current) {
    //         observer.current.disconnect();
    //       }
    //     };
    // }, []);
    /**
     * @param {Vector2} pos 
     * @param {Vector2} velocity 
     * @param {Vector3} color 
     * @param {number} magnitude 
     * @returns {Entity}
     */
    const spawnGravityObject = (pos, velocity, color, magnitude) => {
        if (!entities.current)
            return undefined;
        const gravEntity = instantiateGravity(pos, velocity.multiply(windowRatio), color, magnitude);
        entities.current.addEntity(gravEntity);
        return gravEntity;
    }

    return (
        <div>
            <canvas className={"background "+className} ref={canvasRef} width={resolution.x * windowRatio} height={resolution.y * windowRatio} />
            <div className={"emitterHTML "+className} ref={emitterHTML}>

            </div>
        {/* <div ref={fpsText} style={{position: "absolute", bottom: "0px", right:"1vw", color:"black", background: "white"}}></div> */}
        </div>
    );
});


/**
 * Spawns an emitter entity
 * @param {HTMLDivElement | null} emitterHTML Element to stick emitter to
 * @param {Vector3} color Emitter color in 255RGB
 * @param {number} width Emitter width in pixels
 * @param {number} height Emitter height in pixels
 * @returns {Entity} The emitter's entity instance
 */
function instantiateEmitter (emitterHTML, color, width, height){
    const emitter = new Entity();
    const emitterRenderer = new RoundRecRendererComponent(color, width, height, 30);
    const emitterTransform = new TransformComponent(new Vector2(0, 0), false);
    const emitterFollow = new HTMLfollowComponent(emitterHTML);

    emitter.addComponent(emitterTransform);
    emitter.addComponent(emitterRenderer);
    emitter.addComponent(emitterFollow);
    emitter.addComponent(new EmitterComponent());
    return emitter;
} 

/**
 * Spawns an entity that follow's the cursor and emits gravity
 * @param {Vector2} position Position where the emitter is placed
 * @param {number} gravityMagnitude gravity magnitude
 * @returns {Entity} The cursor's entity instance
 */
function instantiateCursor (position, gravityMagnitude){
    const cursor = new Entity();
    const cursorTransform = new TransformComponent(position);

    cursor.addComponent(cursorTransform);
    cursor.addComponent(new CircleRendererComponent(new Vector3(255, 255, 255), 15, false));
    cursor.addComponent(new GravityComponent(gravityMagnitude, false));
    return cursor;
} 

function instantiateGravity(position, velocity, color, gravityMagnitude){
    const grav = new Entity();
    const gravTransform = new TransformComponent(position, true);
    const gravRender = new CircleRendererComponent(color, 10);
    const gravRb = new RigidbodyComponent(
        gravTransform, 
        velocity,
        1, 
        0.99, 
        false
    );
    const gr = new GravityComponent(gravityMagnitude, true);

    grav.addComponent(gravTransform);
    grav.addComponent(gravRender);
    grav.addComponent(gravRb);
    grav.addComponent(gr);
    // grav.addComponent(ac);
    return grav;
}

/**
 * 
 * @param {Entity} cursor 
 * @returns {Entity}
 */
function instantiateBomb(cursor){
    const bomb = new Entity();
    const bombTransform = new TransformComponent(cursor.getComponent(TransformComponent).position.copy(), false);
    const bombRender = new CircleRendererComponent(new Vector3(200, 0, 0), 6);
    const bombFollow = new FollowComponent(cursor.getComponent(TransformComponent));
    const bombRb = new RigidbodyComponent(
        bombTransform, 
        new Vector2(0, 0),
        1, 
        0.99, 
        true
    );
    const bombLine = new LineRenderComponent(
        cursor.getComponent(TransformComponent).position.copy(),
        new Vector3(255, 255, 255),
        new Vector3(66, 56, 56),
    );
    bombRb.enabled = false;
    const bo = new BombComponent(50, false, false);

    bomb.addComponent(bombTransform);
    bomb.addComponent(bombFollow);
    bomb.addComponent(bombRender);
    bomb.addComponent(bombRb);
    bomb.addComponent(bo);
    bomb.addComponent(bombLine);
    return bomb;
}

export default memo(BackgroundCanvas);

export {ImageRecord};
